<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import SignupForm from '@/components/SignupForm.vue';
import SignInForm from '@/components/SignInForm.vue';
import { useConnectivity } from '@/composables/useConnectivity';
import { webAuthnSupported } from '@/utils/webauthn';

withDefaults(
  defineProps<{
    /** When true, show footer link below the card (prototype login page). */
    standalone?: boolean;
  }>(),
  { standalone: false }
);

const { t } = useI18n();
const { isOnline } = useConnectivity();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const { signupSuccess } = storeToRefs(authStore);

type MainTab = 'password' | 'link' | 'passkey' | 'signup';

const mainTab = ref<MainTab>('password');
const passkeyTabAvailable = computed(() => webAuthnSupported());

function mainTabFromRoute(): MainTab {
  const name = route.name;
  if (name === 'account-login-link') return 'link';
  if (name === 'account-login-passkey') return 'passkey';
  if (name === 'account-signup') return 'signup';
  if (name === 'account-tab') {
    const raw = route.params.section;
    const sec = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : '';
    if (sec === 'passkeys') return 'passkey';
    if (sec === 'password' || sec === 'details') return 'password';
  }
  return 'password';
}

function syncMainTabFromRoute() {
  if (authStore.isAuthenticated) return;

  if (!passkeyTabAvailable.value && route.name === 'account-login-passkey') {
    void router.replace({ path: '/account', query: route.query });
    return;
  }

  mainTab.value = mainTabFromRoute();
}

const loading = computed(
  () => authStore.status === 'uninitialized' || authStore.status === 'loading'
);

const headerTitle = computed(() =>
  mainTab.value === 'signup'
    ? t('account.authCard.createTitle')
    : t('account.authCard.signInTitle')
);

const headerSubtitle = computed(() =>
  mainTab.value === 'signup'
    ? t('account.authCard.createSubtitle')
    : t('account.authCard.signInSubtitle')
);

const footerTerms = computed(() =>
  mainTab.value === 'signup'
    ? t('account.authCard.termsCreate')
    : t('account.authCard.termsSignIn')
);

const signInMode = computed<'password' | 'link' | 'passkey'>(() => {
  if (mainTab.value === 'signup') return 'password';
  return mainTab.value;
});

function loginPathForTab(tab: MainTab): string {
  if (tab === 'password') return '/account';
  if (tab === 'link') return '/account/link';
  if (tab === 'passkey') return '/account/passkey';
  return '/account/signup';
}

function setMainTab(tab: MainTab) {
  if (tab === 'passkey' && !passkeyTabAvailable.value) return;
  mainTab.value = tab;
  if (tab === 'signup') {
    authStore.clearLoginState();
  } else {
    authStore.clearSignupState();
  }
  void router.replace({ path: loginPathForTab(tab), query: route.query });
}

watch(signupSuccess, (ok) => {
  if (!ok) return;
  mainTab.value = 'signup';
  void router.replace({ path: '/account/signup', query: route.query });
}, { immediate: true });

watch(
  () =>
    [
      route.name,
      route.params.section,
      passkeyTabAvailable.value,
      authStore.isAuthenticated,
    ] as const,
  () => {
    syncMainTabFromRoute();
  },
  { immediate: true }
);

onMounted(() => {
  if (authStore.status === 'uninitialized') {
    void authStore.initialize();
  }
});
</script>

<template>
  <div class="account-auth">
    <p v-if="loading" class="auth-subtitle account-auth__loading">Loading session…</p>

    <template v-if="!loading && !authStore.isAuthenticated">
      <div class="account-login-card">
        <header class="account-login-card__header">
          <div class="account-login-card__icon-wrap" aria-hidden="true">
            <svg class="account-login-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <div class="account-login-card__titles">
            <h2 class="account-login-card__title">{{ headerTitle }}</h2>
            <p class="account-login-card__subtitle">{{ headerSubtitle }}</p>
          </div>
        </header>

        <div
          v-if="!isOnline"
          class="account-login-card__offline-callout"
          role="status"
        >
          <p class="account-login-card__offline-title">{{ t('common.connectionRequired') }}</p>
          <p class="account-login-card__offline-detail">{{ t('common.offlineMutationsHint') }}</p>
        </div>

        <div class="account-login-card__tabs" role="tablist" :aria-label="t('account.authCard.signInTitle')">
          <button
            type="button"
            class="account-login-card__tab"
            role="tab"
            :class="{ 'account-login-card__tab--active': mainTab === 'password' }"
            :aria-selected="mainTab === 'password'"
            @click="setMainTab('password')"
          >
            {{ t('account.authCard.tabPassword') }}
          </button>
          <button
            type="button"
            class="account-login-card__tab"
            role="tab"
            :class="{ 'account-login-card__tab--active': mainTab === 'link' }"
            :aria-selected="mainTab === 'link'"
            @click="setMainTab('link')"
          >
            {{ t('account.authCard.tabLink') }}
          </button>
          <button
            type="button"
            class="account-login-card__tab"
            role="tab"
            :disabled="!passkeyTabAvailable"
            :title="!passkeyTabAvailable ? t('account.authCard.passkeyUnavailable') : undefined"
            :class="{ 'account-login-card__tab--active': mainTab === 'passkey' }"
            :aria-selected="mainTab === 'passkey'"
            @click="setMainTab('passkey')"
          >
            {{ t('account.authCard.tabPasskey') }}
          </button>
          <button
            type="button"
            class="account-login-card__tab"
            role="tab"
            :class="{ 'account-login-card__tab--active': mainTab === 'signup' }"
            :aria-selected="mainTab === 'signup'"
            @click="setMainTab('signup')"
          >
            {{ t('account.authCard.tabCreate') }}
          </button>
        </div>

        <div class="account-login-card__body">
          <SignInForm v-show="mainTab !== 'signup'" :mode="signInMode" />
          <SignupForm v-show="mainTab === 'signup'" embedded />
        </div>

        <footer class="account-login-card__footer">
          <p class="account-login-card__legal">{{ footerTerms }}</p>
        </footer>
      </div>

      <p v-if="standalone" class="account-auth__back">
        <router-link class="account-auth__back-link" to="/">{{ t('account.authCard.backToHome') }}</router-link>
      </p>
    </template>
  </div>
</template>

<style scoped>
.account-auth {
  width: 100%;
  min-width: 0;
}

.account-auth__loading {
  margin: 0;
}

.account-login-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: hidden;
}

.account-login-card__offline-callout {
  margin: 0;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface-muted, #fafafa);
}
.account-login-card__offline-title {
  margin: 0 0 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text, #18181b);
}
.account-login-card__offline-detail {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--color-text-muted, #52525b);
}

.account-login-card__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1.5rem;
  border-bottom: 1px solid var(--color-border);
}

@media (min-width: 1024px) {
  .account-login-card__header {
    padding: 1.5rem 2rem;
  }
}

.account-login-card__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  flex-shrink: 0;
  border-radius: 6px;
  background: color-mix(in srgb, var(--color-primary) 10%, transparent);
  color: var(--color-primary);
}

.account-login-card__icon {
  width: 1.5rem;
  height: 1.5rem;
}

.account-login-card__titles {
  min-width: 0;
}

.account-login-card__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--color-text);
  font-family: var(--font-sans);
}

@media (min-width: 1024px) {
  .account-login-card__title {
    font-size: 1.5rem;
  }
}

.account-login-card__subtitle {
  margin: 0.125rem 0 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: var(--color-text-muted);
}

.account-login-card__tabs {
  display: flex;
  border-bottom: 1px solid var(--color-border);
}

.account-login-card__tab {
  flex: 1;
  margin: 0;
  padding: 0.75rem 0.5rem;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
  color: var(--color-text-muted);
  cursor: pointer;
  transition:
    color 0.15s ease,
    background-color 0.15s ease,
    border-color 0.15s ease;
}

.account-login-card__tab:hover:not(:disabled) {
  color: var(--color-text);
  background: color-mix(in srgb, var(--color-bg-muted) 30%, transparent);
}

.account-login-card__tab:focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--color-focus-ring);
  z-index: 1;
}

.account-login-card__tab:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.account-login-card__tab--active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
  background: color-mix(in srgb, var(--color-primary) 5%, transparent);
}

.account-login-card__body {
  padding: 1.5rem 1.5rem;
}

@media (min-width: 1024px) {
  .account-login-card__body {
    padding: 2rem;
  }
}

.account-login-card__footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--color-border);
}

@media (min-width: 1024px) {
  .account-login-card__footer {
    padding: 1rem 2rem;
  }
}

.account-login-card__legal {
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--color-text-muted);
  text-align: center;
}

.account-auth__back {
  margin: 1.5rem 0 0;
  text-align: center;
}

.account-auth__back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
  color: var(--color-text-muted);
  text-decoration: none;
  transition: color 0.15s ease;
}

.account-auth__back-link:hover {
  color: var(--color-primary);
}
</style>
